import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import './TeacherCreateTest.css'
import LoadingScreen from '../components/LoadingScreen'
import { testTemplates } from '../utils/testTemplates'

const TeacherCreateTest = () => {
  const history = useHistory()
  const { testId } = useParams()
  const isEditMode = Boolean(testId)
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: 60,
    passing_score: 60,
    total_marks: 100,
    instructions: '',
    start_time: '',
    end_time: '',
    status: 'draft',
    proctoring_enabled: true,
    face_detection: true,
    tab_switch_detection: true,
    max_violations: 3,
    max_attempts: 1
  })
  const [questions, setQuestions] = useState([])
  const [numQuestionsInput, setNumQuestionsInput] = useState(10)
  const [defaultQuestionType, setDefaultQuestionType] = useState('multiple_choice')
  const [typeLocked, setTypeLocked] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [expandedQuestions, setExpandedQuestions] = useState({})
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(isEditMode)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchTestData = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch(`/api/teacher/tests/${testId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          const test = data.test
          
          // Format datetime values for datetime-local input
          const formatDateTime = (dateStr) => {
            if (!dateStr) return ''
            const date = new Date(dateStr)
            return date.toISOString().slice(0, 16)
          }

          const formattedStartTime = formatDateTime(test.start_time)
          const formattedEndTime = formatDateTime(test.end_time)
          
          setFormData({
            title: test.title || '',
            description: test.description || '',
            duration: test.duration_minutes || 60,
            passing_score: test.passing_score || 60,
            total_marks: test.total_points || 100,
            instructions: test.instructions || '',
            start_time: formattedStartTime,
            end_time: formattedEndTime,
            status: test.status || 'draft',
            proctoring_enabled: test.proctoring_enabled !== false,
            face_detection: test.face_detection !== false,
            tab_switch_detection: test.tab_switch_detection !== false,
            max_violations: test.max_violations || 3,
            max_attempts: test.max_attempts || 1
          })
          
          // Enable schedule if times are set
          if (formattedStartTime || formattedEndTime) {
            setScheduleEnabled(true)
          }
        } else {
          setError('Failed to load test data')
        }
      } catch (err) {
        setError('Network error loading test')
      } finally {
        setFetchLoading(false)
      }
    }

    if (isEditMode) {
      fetchTestData()
    }
  }, [testId, isEditMode])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleLoadTemplate = (template) => {
    setFormData({
      ...formData,
      title: template.title,
      description: template.description,
      duration: template.duration,
      passing_score: template.passing_score,
      total_marks: template.total_marks,
      instructions: template.instructions
    })
    setQuestions(template.questions.map((q, idx) => ({
      ...q,
      id: `q-${Date.now()}-${idx}`
    })))
    setShowTemplateModal(false)
  }

  const generateEmptyQuestions = (count) => {
    const newQuestions = []
    const currentCount = questions.length
    for (let i = 0; i < count; i++) {
      const options = defaultQuestionType === 'true_false' ? ['True', 'False'] : ['', '', '', '']
      newQuestions.push({
        id: `q-${Date.now()}-${i}`,
        question_text: '',
        question_type: defaultQuestionType,
        options: options,
        correct_answer: 0,
        points: Math.floor(100 / count),
        order: currentCount + i
      })
    }
    setQuestions(prev => [...prev, ...newQuestions])
  }

  const updateQuestion = (id, field, value) => {
    setQuestions(prev => prev.map(q => 
      q.id === id ? { ...q, [field]: value } : q
    ))
  }

  const updateQuestionOption = (id, optionIndex, value) => {
    setQuestions(prev => prev.map(q => {
      if (q.id === id) {
        const newOptions = [...q.options]
        newOptions[optionIndex] = value
        return { ...q, options: newOptions }
      }
      return q
    }))
  }

  const removeQuestion = (id) => {
    setQuestions(prev => prev.filter(q => q.id !== id))
  }

  const addQuestion = () => {
    const options = defaultQuestionType === 'true_false' ? ['True', 'False'] : ['', '', '', '']
    const newQuestion = {
      id: `q-${Date.now()}`,
      question_text: '',
      question_type: defaultQuestionType,
      options: options,
      correct_answer: 0,
      points: 10,
      order: questions.length
    }
    setQuestions(prev => [...prev, newQuestion])
    setExpandedQuestions(prev => ({ ...prev, [newQuestion.id]: true }))
  }

  const toggleQuestionExpanded = (id) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const handleQuestionTypeChange = (id, type) => {
    setQuestions(prev => prev.map(q => {
      if (q.id === id) {
        if (type === 'true_false') {
          return {
            ...q,
            question_type: type,
            options: ['True', 'False'],
            correct_answer: 0
          }
        } else {
          return {
            ...q,
            question_type: type,
            options: ['', '', '', ''],
            correct_answer: 0
          }
        }
      }
      return q
    }))
  }

  const handleSchedulePreset = (preset) => {
    const now = new Date()
    let startTime = new Date(now)
    let endTime = new Date(now)

    switch (preset) {
      case 'now':
        startTime = now
        endTime = new Date(now.getTime() + formData.duration * 60 * 1000)
        break
      case '1hour':
        startTime = new Date(now.getTime() + 60 * 60 * 1000)
        endTime = new Date(startTime.getTime() + formData.duration * 60 * 1000)
        break
      case 'tomorrow':
        startTime = new Date(now)
        startTime.setDate(startTime.getDate() + 1)
        startTime.setHours(9, 0, 0, 0)
        endTime = new Date(startTime.getTime() + formData.duration * 60 * 1000)
        break
      case 'nextweek':
        startTime = new Date(now)
        startTime.setDate(startTime.getDate() + 7)
        startTime.setHours(9, 0, 0, 0)
        endTime = new Date(startTime.getTime() + formData.duration * 60 * 1000)
        break
      default:
        return
    }

    const formatDateTime = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    }

    setFormData(prev => ({
      ...prev,
      start_time: formatDateTime(startTime),
      end_time: formatDateTime(endTime)
    }))
    setScheduleEnabled(true)
  }

  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value
    setFormData(prev => {
      // Auto-calculate end time if start time changes and end time is empty
      let newEndTime = prev.end_time
      if (newStartTime && !prev.end_time) {
        const start = new Date(newStartTime)
        const end = new Date(start.getTime() + prev.duration * 60 * 1000)
        const formatDateTime = (date) => {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          const hours = String(date.getHours()).padStart(2, '0')
          const minutes = String(date.getMinutes()).padStart(2, '0')
          return `${year}-${month}-${day}T${hours}:${minutes}`
        }
        newEndTime = formatDateTime(end)
      }
      return {
        ...prev,
        start_time: newStartTime,
        end_time: newEndTime
      }
    })
    if (newStartTime) {
      setScheduleEnabled(true)
    }
  }

  const handleEndTimeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      end_time: e.target.value
    }))
    if (e.target.value) {
      setScheduleEnabled(true)
    }
  }

  const validateSchedule = () => {
    if (formData.start_time && formData.end_time) {
      const start = new Date(formData.start_time)
      const end = new Date(formData.end_time)
      if (end <= start) {
        setError('End time must be after start time')
        return false
      }
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validate schedule
    if (!validateSchedule()) {
      setLoading(false)
      return
    }

    // Validate questions
    if (!isEditMode && questions.length === 0) {
      setError('Please add at least one question')
      setLoading(false)
      return
    }

    // Validate each question
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i]
      if (!q.question_text.trim()) {
        setError(`Question ${i + 1} is missing text`)
        setLoading(false)
        return
      }
      if (q.options.some(opt => !opt.trim())) {
        setError(`Question ${i + 1} has empty options`)
        setLoading(false)
        return
      }
    }

    try {
      const token = localStorage.getItem('access_token')
      const url = isEditMode 
        ? `/api/teacher/tests/${testId}`
        : '/api/teacher/tests'
      
    const payload = {
      ...formData,
      duration: parseInt(formData.duration) || 60,
      passing_score: parseInt(formData.passing_score) || 0,
      total_marks: parseInt(formData.total_marks) || 100,
      max_violations: parseInt(formData.max_violations) || 3,
      max_attempts: parseInt(formData.max_attempts) || 1,
      questions: questions.map((q, idx) => ({
        question_text: q.question_text,
        question_type: q.question_type,
        options: q.options,
        correct_answer: q.correct_answer,
        points: q.points,
        order: idx
      }))
    }
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        const data = await response.json()
        history.push(`/teacher/tests`)
      } else {
        const errorData = await response.json()
        // Handle Flask-JWT-Extended 'msg' key
        const errorMessage = errorData.message || errorData.error || errorData.msg || `Failed to ${isEditMode ? 'update' : 'create'} test`
        
        if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('token')) {
          setError('Session expired. Please open a new tab, log in again, then return here and try clicking Create Test again.')
        } else {
          setError(errorMessage)
        }
      }
    } catch (err) {
      setError('Network error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  if (fetchLoading) {
    return <LoadingScreen message="Loading Test Data" />
  }

  return (
    <div className="create-test-container">
      <header className="page-header">
        <h1>{isEditMode ? 'Edit Test' : 'Create New Test'}</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {!isEditMode && (
            <button 
              type="button"
              onClick={() => setShowTemplateModal(true)} 
              className="template-button"
            >
              📋 Load Template
            </button>
          )}
          <button onClick={() => history.push('/teacher-dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </header>

      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Select a Template</h2>
            <div className="template-grid">
              {testTemplates.map(template => (
                <div key={template.id} className="template-card" onClick={() => handleLoadTemplate(template)}>
                  <h3>{template.name}</h3>
                  <p className="template-category">{template.category}</p>
                  <p className="template-description">{template.description}</p>
                  <div className="template-meta">
                    <span>{template.questions.length} questions</span>
                    <span>{template.duration} min</span>
                  </div>
                </div>
              ))}
            </div>
            <button className="close-modal-button" onClick={() => setShowTemplateModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      <div className="create-test-content">
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="test-form">
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="title">Test Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Final Exam - Mathematics"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
                placeholder="Describe the test content and objectives"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration">Duration (minutes) *</label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="total_marks">Total Marks *</label>
                <input
                  type="number"
                  id="total_marks"
                  name="total_marks"
                  value={formData.total_marks}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="passing_score">Passing Score (%) *</label>
                <input
                  type="number"
                  id="passing_score"
                  name="passing_score"
                  value={formData.passing_score}
                  onChange={handleChange}
                  required
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="schedule-header">
              <h2>Schedule</h2>
              <label className="schedule-toggle">
                <input
                  type="checkbox"
                  checked={scheduleEnabled || !!formData.start_time || !!formData.end_time}
                  onChange={(e) => {
                    setScheduleEnabled(e.target.checked)
                    if (!e.target.checked) {
                      setFormData(prev => ({ ...prev, start_time: '', end_time: '' }))
                    }
                  }}
                />
                <span>Enable Scheduling</span>
              </label>
            </div>

            {(scheduleEnabled || formData.start_time || formData.end_time) && (
              <>
                <div className="schedule-presets">
                  <span className="preset-label">Quick Presets:</span>
                  <div className="preset-buttons-group">
                    <button type="button" onClick={() => handleSchedulePreset('now')} className="preset-btn-small">
                      Start Now
                    </button>
                    <button type="button" onClick={() => handleSchedulePreset('1hour')} className="preset-btn-small">
                      In 1 Hour
                    </button>
                    <button type="button" onClick={() => handleSchedulePreset('tomorrow')} className="preset-btn-small">
                      Tomorrow 9 AM
                    </button>
                    <button type="button" onClick={() => handleSchedulePreset('nextweek')} className="preset-btn-small">
                      Next Week
                    </button>
                  </div>
                </div>

                <div className="form-row schedule-row">
                  <div className="form-group schedule-input-group">
                    <label htmlFor="start_time">
                      <span className="label-icon">📅</span>
                      Start Time
                    </label>
                    <input
                      type="datetime-local"
                      id="start_time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleStartTimeChange}
                      className="schedule-input"
                      min={new Date().toISOString().slice(0, 16)}
                    />
                    {formData.start_time && (
                      <p className="schedule-hint">
                        {new Date(formData.start_time).toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div className="schedule-arrow">→</div>

                  <div className="form-group schedule-input-group">
                    <label htmlFor="end_time">
                      <span className="label-icon">📅</span>
                      End Time
                    </label>
                    <input
                      type="datetime-local"
                      id="end_time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleEndTimeChange}
                      className="schedule-input"
                      min={formData.start_time || new Date().toISOString().slice(0, 16)}
                    />
                    {formData.end_time && (
                      <p className="schedule-hint">
                        {new Date(formData.end_time).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                {formData.start_time && formData.end_time && (
                  <div className="schedule-summary">
                    <div className="summary-item">
                      <span className="summary-label">Duration:</span>
                      <span className="summary-value">
                        {Math.round((new Date(formData.end_time) - new Date(formData.start_time)) / (1000 * 60))} minutes
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Time Until Start:</span>
                      <span className="summary-value">
                        {(() => {
                          const now = new Date()
                          const start = new Date(formData.start_time)
                          const diff = start - now
                          if (diff < 0) return 'Started'
                          const hours = Math.floor(diff / (1000 * 60 * 60))
                          const days = Math.floor(hours / 24)
                          if (days > 0) return `${days} day${days > 1 ? 's' : ''}`
                          if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`
                          const minutes = Math.floor(diff / (1000 * 60))
                          return `${minutes} minute${minutes > 1 ? 's' : ''}`
                        })()}
                      </span>
                    </div>
                  </div>
                )}

                {formData.start_time && formData.end_time && new Date(formData.end_time) <= new Date(formData.start_time) && (
                  <div className="schedule-error">
                    ⚠️ End time must be after start time
                  </div>
                )}
              </>
            )}

            {!(scheduleEnabled || formData.start_time || formData.end_time) && (
              <p className="schedule-help-text">
                💡 Optional: Set a schedule to control when students can take this test. Leave empty to allow immediate access.
              </p>
            )}
          </div>

          <div className="form-section">
            <h2>Instructions</h2>

            <div className="form-group">
              <label htmlFor="instructions">Test Instructions</label>
              <textarea
                id="instructions"
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                rows="6"
                placeholder="Enter instructions for students taking this test"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Test Status</h2>

            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="draft">Draft (Not visible to students)</option>
                <option value="active">Active (Visible to students)</option>
                <option value="completed">Completed</option>
              </select>
              <p className="field-help">Set to "Active" to make this test available to students</p>
            </div>
          </div>

          <div className="form-section">
            <h2>Proctoring Settings</h2>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="proctoring_enabled"
                  checked={formData.proctoring_enabled}
                  onChange={handleChange}
                />
                <span>Enable Proctoring</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="face_detection"
                  checked={formData.face_detection}
                  onChange={handleChange}
                  disabled={!formData.proctoring_enabled}
                />
                <span>Face Detection</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="tab_switch_detection"
                  checked={formData.tab_switch_detection}
                  onChange={handleChange}
                  disabled={!formData.proctoring_enabled}
                />
                <span>Tab Switch Detection</span>
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="max_violations">Maximum Violations Allowed</label>
              <input
                type="number"
                id="max_violations"
                name="max_violations"
                value={formData.max_violations}
                onChange={handleChange}
                min="1"
                disabled={!formData.proctoring_enabled}
              />
              <small className="form-help">Test will auto-submit when this limit is reached</small>
            </div>

            <div className="form-group">
              <label htmlFor="max_attempts">Maximum Attempts Per Student</label>
              <input
                type="number"
                id="max_attempts"
                name="max_attempts"
                value={formData.max_attempts}
                onChange={handleChange}
                min="1"
                max="10"
              />
              <small className="form-help">How many times a student can take this test</small>
            </div>
          </div>

          {!isEditMode && (
            <div className="form-section">
              <h2>Questions <span className="question-count-badge">Total: {questions.length}</span></h2>
              
              <div className="question-type-selector">
                <div className="type-selector-header">
                  <label htmlFor="defaultQuestionType">Default Question Type:</label>
                  <label className="lock-toggle">
                    <input
                      type="checkbox"
                      checked={typeLocked}
                      onChange={(e) => setTypeLocked(e.target.checked)}
                    />
                    <span>🔒 Enforce this type for all questions</span>
                  </label>
                </div>
                <select
                  id="defaultQuestionType"
                  value={defaultQuestionType}
                  onChange={(e) => {
                    setDefaultQuestionType(e.target.value)
                    if (typeLocked) {
                      // Update all existing questions if locked
                      setQuestions(prev => prev.map(q => ({
                        ...q,
                        question_type: e.target.value,
                        options: e.target.value === 'true_false' ? ['True', 'False'] : ['', '', '', '']
                      })))
                    }
                  }}
                  className="question-type-select"
                >
                  <option value="multiple_choice">Multiple Choice (MCQ - 4 options)</option>
                  <option value="true_false">True/False (BCQ - 2 options)</option>
                </select>
                <p className="field-help">
                  {typeLocked 
                    ? "Type is locked. All questions will use this type." 
                    : "New questions will use this type. You can still change individual questions."}
                </p>
              </div>
              
              <div className="question-presets">
                <label>Question Generator (Add Batch):</label>
                <p className="field-help" style={{ marginBottom: '1rem' }}>
                  Add multiple empty questions at once.
                </p>
                <div className="preset-buttons">
                  <button type="button" onClick={() => generateEmptyQuestions(10)} className="preset-btn">+ Add 10</button>
                  <button type="button" onClick={() => generateEmptyQuestions(20)} className="preset-btn">+ Add 20</button>
                  <div className="custom-input-group">
                    <input 
                      type="number" 
                      value={numQuestionsInput}
                      onChange={(e) => setNumQuestionsInput(parseInt(e.target.value) || 0)}
                      min="1"
                      max="200"
                      className="custom-num-input"
                      placeholder="Qty"
                    />
                    <button type="button" onClick={() => generateEmptyQuestions(numQuestionsInput)} className="preset-btn">
                      + Add {numQuestionsInput} Questions
                    </button>
                  </div>
                </div>
              </div>

              <div className="questions-list">
                {questions.map((question, index) => (
                  <div key={question.id} className="question-card-builder">
                    <div className="question-card-header" onClick={() => toggleQuestionExpanded(question.id)}>
                      <div className="question-card-title">
                        <span className="question-number">Question {index + 1}</span>
                        {question.question_text && (
                          <span className="question-preview">{question.question_text.substring(0, 50)}{question.question_text.length > 50 ? '...' : ''}</span>
                        )}
                      </div>
                      <div className="question-card-actions">
                        <button 
                          type="button" 
                          onClick={(e) => { e.stopPropagation(); removeQuestion(question.id); }}
                          className="remove-question-btn"
                        >
                          🗑️
                        </button>
                        <span className="expand-icon">{expandedQuestions[question.id] ? '▼' : '▶'}</span>
                      </div>
                    </div>

                    {expandedQuestions[question.id] && (
                      <div className="question-card-body">
                        <div className="form-group">
                          <label>Question Text *</label>
                          <textarea
                            value={question.question_text}
                            onChange={(e) => updateQuestion(question.id, 'question_text', e.target.value)}
                            rows="3"
                            placeholder="Enter your question here"
                            required
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Question Type</label>
                            {typeLocked ? (
                              <div className="locked-type-display">
                                🔒 {question.question_type === 'multiple_choice' ? 'Multiple Choice (MCQ)' : 'True/False (BCQ)'}
                              </div>
                            ) : (
                              <select
                                value={question.question_type}
                                onChange={(e) => handleQuestionTypeChange(question.id, e.target.value)}
                              >
                                <option value="multiple_choice">Multiple Choice (MCQ - 4 options)</option>
                                <option value="true_false">True/False (BCQ - 2 options)</option>
                              </select>
                            )}
                          </div>

                          <div className="form-group">
                            <label>Points</label>
                            <input
                              type="number"
                              value={question.points}
                              onChange={(e) => updateQuestion(question.id, 'points', parseInt(e.target.value) || 0)}
                              min="1"
                            />
                          </div>
                        </div>

                        <div className="options-section">
                          <label>Options *</label>
                          {question.options.map((option, optIndex) => (
                            <div key={optIndex} className="option-input-group">
                              <input
                                type="radio"
                                name={`correct-${question.id}`}
                                checked={question.correct_answer === optIndex}
                                onChange={() => updateQuestion(question.id, 'correct_answer', optIndex)}
                                title="Mark as correct answer"
                              />
                              <span className="option-label">{String.fromCharCode(65 + optIndex)}.</span>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => updateQuestionOption(question.id, optIndex, e.target.value)}
                                placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                                className="option-input"
                                required
                              />
                            </div>
                          ))}
                          <p className="option-help">✓ Select the radio button to mark the correct answer</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {questions.length === 0 && (
                <div className="no-questions-container">
                  <p className="no-questions-message">
                    Get started by adding questions manually or using a template
                  </p>
                  <div className="question-action-buttons">
                    <button type="button" onClick={addQuestion} className="add-first-question-btn">
                      ➕ Add Question Manually
                    </button>
                    <span className="or-divider">or</span>
                    <button 
                      type="button"
                      onClick={() => setShowTemplateModal(true)} 
                      className="template-button-inline"
                    >
                      📋 Load Template
                    </button>
                  </div>
                </div>
              )}

              {questions.length > 0 && (
                <button type="button" onClick={addQuestion} className="add-question-btn">
                  + Add Another Question
                </button>
              )}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => history.push('/teacher-dashboard')}
              className="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading 
                ? (isEditMode ? 'Updating...' : 'Creating...') 
                : (isEditMode ? 'Update Test' : 'Create Test')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TeacherCreateTest
